import { io, Socket } from 'socket.io-client';
import type { Message, ConnectionStatus } from '../types';

export class SocketClient {
  private socket: Socket | null = null;
  private apiUrl: string;
  private apiKey: string;
  private userId?: string;
  private onStatusChange?: (status: ConnectionStatus) => void;
  private onMessage?: (message: Message) => void;
  private onTypingStart?: (userId?: string) => void;
  private onTypingStop?: (userId?: string) => void;

  constructor(apiUrl: string, apiKey: string, userId?: string) {
    this.apiUrl = apiUrl.replace(/\/$/, ''); // Remove trailing slash
    this.apiKey = apiKey;
    this.userId = userId;
  }

  connect() {
    if (this.socket?.connected) {
      return;
    }

    const wsUrl = this.apiUrl.replace(/^https?/, 'ws').replace(/^http/, 'ws');
    
    this.socket = io(wsUrl, {
      query: {
        apiKey: this.apiKey,
        ...(this.userId && { userId: this.userId }),
      },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5,
    });

    this.setupEventHandlers();
  }

  private setupEventHandlers() {
    if (!this.socket) return;

    this.socket.on('connect', () => {
      this.onStatusChange?.('connected');
    });

    this.socket.on('disconnect', () => {
      this.onStatusChange?.('disconnected');
    });

    this.socket.on('connect_error', () => {
      this.onStatusChange?.('error');
    });

    this.socket.on('connection:status', (data: { status: string }) => {
      if (data.status === 'connected' || data.status === 'reconnected') {
        this.onStatusChange?.('connected');
      }
    });

    this.socket.on('message:new', (data: { type: string; message: Message }) => {
      this.onMessage?.(data.message);
    });

    this.socket.on('typing:start', (data: { userId?: string }) => {
      this.onTypingStart?.(data.userId);
    });

    this.socket.on('typing:stop', (data: { userId?: string }) => {
      this.onTypingStop?.(data.userId);
    });
  }

  joinConversation(conversationId: string) {
    this.socket?.emit('join:conversation', { conversationId });
  }

  leaveConversation(conversationId: string) {
    this.socket?.emit('leave:conversation', { conversationId });
  }

  startTyping(conversationId: string) {
    this.socket?.emit('typing:start', { conversationId });
  }

  stopTyping(conversationId: string) {
    this.socket?.emit('typing:stop', { conversationId });
  }

  on(event: 'status', handler: (status: ConnectionStatus) => void): void;
  on(event: 'message', handler: (message: Message) => void): void;
  on(event: 'typing:start', handler: (userId?: string) => void): void;
  on(event: 'typing:stop', handler: (userId?: string) => void): void;
  on(event: string, handler: any): void {
    switch (event) {
      case 'status':
        this.onStatusChange = handler;
        break;
      case 'message':
        this.onMessage = handler;
        break;
      case 'typing:start':
        this.onTypingStart = handler;
        break;
      case 'typing:stop':
        this.onTypingStop = handler;
        break;
    }
  }

  disconnect() {
    this.socket?.disconnect();
    this.socket = null;
  }

  isConnected(): boolean {
    return this.socket?.connected || false;
  }
}

